import React, { useEffect, useState } from 'react';
import { Bar, ChartProps } from 'react-chartjs-2';
import { ChartData } from 'chart.js/dist/types';
import getOptionLabel from '@utils/getOptionLabel';
import ChartContainer from '@components/charts/ChartContainer';
import getAxisMaxValue from '@utils/getAxisMaxValue';
import { MRRRiskLevelCountsByFacility } from '@api/MedicalRRService/getRiskLevelCountsByFacility';
import calculateAxisMaxValue from '@utils/calculateAxisMaxValue';
import { RiskLevel, RiskLevelColors } from '@declarations/common/enums/RiskLevel';
import RiskLevelOptions from '@constants/options/RiskLeveOptions';
import getFacilityLabels from '@utils/getFacilityLabels';

const options = (dataLength: number): ChartProps<'bar'>['options'] => ({
  maintainAspectRatio: false,
  scales: {
    y: {
      ticks: {stepSize: 1},
      max: getAxisMaxValue(dataLength),
    },
  },
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'MRR Risk Status',
      font: {
        size: 20,
      },
    },
  },
});

type MMRsCountByRiskLevelProps = {
  data: MRRRiskLevelCountsByFacility[] | null;
};

function MMRsCountByRiskLevel({data}: MMRsCountByRiskLevelProps) {
  const [chartData, setChartData] = useState<ChartData<'bar'> | null>(null);

  useEffect(() => {
    if (data) {
      setChartData({
        labels: getFacilityLabels(data),
        datasets: Object.values(RiskLevel).map((value) => ({
          label: getOptionLabel(RiskLevelOptions, value),
          data: data.map(d => d.countsByRiskLevel[value] || 0),
          backgroundColor: RiskLevelColors[value],
          stack: 'x',
          barPercentage: 0.4,
        })),
      });
    } else {
      setChartData(null);
    }
  }, [data]);
  const maxXValue: number = data ? calculateAxisMaxValue(data, 'countsByRiskLevel') : 0;
  return (
    <ChartContainer loading={!chartData}>
      <Bar data={chartData!} options={options(maxXValue)}/>
    </ChartContainer>
  );
}

export default React.memo(MMRsCountByRiskLevel);
